/**
 * Starter EmptyState — centered icon + title + description + actions.
 *
 * Per the design's iconography rule, empty states render a single 48px
 * outline icon at `--fg-tertiary` (never coloured, never a cartoon).
 * Pass any Lucide icon component (or other) as `icon` — it inherits
 * `currentColor`, so no need to set its colour.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './EmptyState.module.css';

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Icon rendered at 48px in the muted tertiary tone. */
  readonly icon?: ReactNode;
  /** Headline title (uses Libre Baskerville via the .title CSS rule). */
  readonly title: ReactNode;
  /** Supporting paragraph (max ~25rem reading width). */
  readonly description?: ReactNode;
  /** Optional action area — typically one or two `<Button>`s. */
  readonly actions?: ReactNode;
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyState(
    { icon, title, description, actions, className, ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={[styles.root, className].filter(Boolean).join(' ')}
        {...rest}
      >
        {icon !== undefined ? <span className={styles.icon}>{icon}</span> : null}
        <h2 className={styles.title}>{title}</h2>
        {description !== undefined ? (
          <p className={styles.description}>{description}</p>
        ) : null}
        {actions !== undefined ? (
          <div className={styles.actions}>{actions}</div>
        ) : null}
      </div>
    );
  },
);
